import { Button, Collapse, Divider, Form } from 'antd';
import { useState } from 'react';
import { showToastError } from 'src/utilities/errortoast';
import FieldsBlock from './FieldsBlock';
import OrganizationSelector from './OrganizationSelector';
import { useEhrOnboarding } from './useEhrOnboarding';

// Generic per-EHR onboarding card. Reads its layout from a registry entry,
// so adding a new EHR is a single file change (`ehrOnboardingRegistry.jsx`).
//
// Layout:
//   1. Optional "how to" collapse with EHR-specific help.
//   2. Organization picker.
//   3. Credentials fields (if `entry.credentials` is set).
//   4. Onboarding fields (always rendered).
//   5. Submit button -> pushes credentials (if any) then calls onboarding.
const EhrOnboardingCard = ({ entry }) => {
  const [form] = Form.useForm();
  const [orgUuid, setOrgUuid] = useState(null);

  const { submit, isSubmitting } = useEhrOnboarding(entry);

  if (!entry) return null;

  const handleSubmit = async () => {
    if (!orgUuid) {
      showToastError('Please select an organization first');
      return;
    }
    try {
      const values = await form.validateFields();
      const result = await submit({
        orgUuid,
        credentials: values?.credentials,
        onboarding: values?.onboarding,
      });
      if (result.success) {
        form.resetFields();
      }
    } catch {
      // antd's validateFields rejects with the validation report; the inline
      // field errors are enough UX-wise, no toast needed.
    }
  };

  const hasCredentials = Boolean(entry.credentials);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {entry.iconUrl && (
          <img
            src={entry.iconUrl}
            alt={`${entry.label} logo`}
            className="w-8 h-8 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <div className="text-lg font-bold font-sans">
          Onboard organization to {entry.label}
        </div>
      </div>

      {entry.howTo && (
        <Collapse
          className="bg-blue-50"
          items={[
            {
              key: 'how-to',
              label: (
                <span className="font-semibold">
                  How {entry.label} onboarding works
                </span>
              ),
              children: entry.howTo,
            },
          ]}
        />
      )}

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-gray-700">
          Organization
        </label>
        <OrganizationSelector
          value={orgUuid}
          onChange={setOrgUuid}
          disabled={isSubmitting}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        disabled={isSubmitting}
        className="flex flex-col gap-1"
      >
        {hasCredentials && (
          <>
            <Divider orientation="left" plain>
              <span className="text-sm text-gray-500">Credentials</span>
            </Divider>
            <FieldsBlock
              fields={entry.credentials.fields}
              namePath={['credentials']}
              disabled={isSubmitting}
            />
          </>
        )}

        <Divider orientation="left" plain>
          <span className="text-sm text-gray-500">Onboarding</span>
        </Divider>
        <FieldsBlock
          fields={entry.onboarding.fields}
          namePath={['onboarding']}
          disabled={isSubmitting}
        />
      </Form>

      <Button
        type="primary"
        size="large"
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={!orgUuid}
        className="self-start"
      >
        {hasCredentials
          ? `Save credentials & onboard ${entry.label}`
          : `Onboard ${entry.label}`}
      </Button>
    </div>
  );
};

export default EhrOnboardingCard;
